/**
 * MOBILE NAVIGATION DRAWER - Version 5.5
 * Features: Immersive Header, Smart Haptic Feedback, Search Trigger, Stagger Animations, Localization Auto-submit
 */

class MobileNavDrawer {
  constructor() {
    this.drawer = document.getElementById('mobile-nav-drawer');
    this.trigger = document.querySelector('.mobile-nav-trigger'); 
    
    // Elements internes
    this.header = null;
    this.closeBtn = null;
    this.backBtn = null;
    this.backdrop = null;
    this.panels = [];
    this.breadcrumbs = null;
    this.searchTrigger = null;
    this.localizationSelect = null;
    
    // State
    this.currentPanel = null;
    this.panelHistory = ['root'];
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.triggerElement = null; 

    if (this.drawer && this.trigger) {
      this.init();
    }
  }
  
  init() {
    // Cache DOM elements
    this.header = this.drawer.querySelector('.mobile-nav-drawer__header');
    this.closeBtn = this.drawer.querySelector('.mobile-nav-drawer__close');
    this.backBtn = this.drawer.querySelector('.mobile-nav-panel__back');
    this.backdrop = this.drawer.querySelector('.mobile-nav-drawer__backdrop');
    this.panels = Array.from(this.drawer.querySelectorAll('.mobile-nav-panel'));
    this.breadcrumbs = this.drawer.querySelector('.mobile-nav-breadcrumbs');
    this.searchTrigger = this.drawer.querySelector('.js-search-trigger');
    this.localizationSelect = this.drawer.querySelector('.mobile-nav-localization__select');
    
    // Initial State
    this.currentPanel = this.drawer.querySelector('.mobile-nav-panel[data-panel="root"]');
    this.panelHistory = ['root'];
    
    // Initialise l'état du header (Transparent au début car Root)
    this.updateHeaderState('root');

    // Observer titre Menu
    this.initBannerTitleObserver();
    
    this.bindEvents();
  }
  
  bindEvents() {
    // Open trigger
    this.trigger.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggle();
      this.triggerHaptic(); // Feedback : Ouverture
    });

    // Close actions
    this.closeBtn.addEventListener('click', () => {
      this.close();
      this.triggerHaptic(); // Feedback : Fermeture
    });
    
    this.backdrop.addEventListener('click', () => {
      this.close();
      // Pas de haptic sur le backdrop
    });
    
    // Navigation Retour
    this.backBtn.addEventListener('click', () => {
      this.goBack();
      this.triggerHaptic(); // Feedback : Retour arrière
    });
    
    // Trigger Recherche
    if (this.searchTrigger) {
      this.searchTrigger.addEventListener('click', () => {
        this.triggerHaptic(); // Feedback : Action
        this.close();
        // L'overlay de recherche externe prend le relais
      });
    }

    // Sécurité pour le Selecteur de Langue (Auto-submit)
    if (this.localizationSelect) {
      this.localizationSelect.addEventListener('change', function() {
        const form = this.closest('form');
        if (form) form.submit();
      });
    }

    // Panel Navigation (Navigation vers sous-catégorie)
    this.drawer.addEventListener('click', (e) => {
      const panelTrigger = e.target.closest('[data-panel-trigger]');
      
      if (panelTrigger) {
        e.preventDefault();
        const targetPanelId = panelTrigger.getAttribute('data-panel-trigger');
        const title = panelTrigger.getAttribute('data-title');
        
        this.navigateToPanel(targetPanelId, title);
        this.triggerHaptic(); // Feedback : Entrée catégorie
      }
    });
    
    // Breadcrumb Navigation
    this.breadcrumbs.addEventListener('click', (e) => {
      const breadcrumb = e.target.closest('.mobile-nav-breadcrumb');
      if (breadcrumb && !breadcrumb.classList.contains('is-active')) {
        const level = parseInt(breadcrumb.getAttribute('data-level'), 10);
        this.navigateToLevel(level);
      }
    });
    
    // Swipe Gesture (Touch)
    this.drawer.addEventListener('touchstart', (e) => {
      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
    }, { passive: true });
    
    this.drawer.addEventListener('touchend', (e) => {
      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;
      const diffX = touchEndX - this.touchStartX;
      const diffY = touchEndY - this.touchStartY;
      
      // Swipe vers la droite (Retour)
      if (Math.abs(diffX) > Math.abs(diffY) && diffX > 80) {
        this.triggerHaptic();
        if (this.panelHistory.length > 1) {
          this.goBack();
        } else {
          this.close();
        }
      }
    }, { passive: true });
    
    // Keyboard Accessibility (Escape)
    document.addEventListener('keydown', (e) => {
      if (this.drawer.classList.contains('is-open')) {
        if (e.key === 'Escape') {
          this.close();
        }
      }
    });
  }

  /* ==============================
     SMART HAPTIC FEEDBACK
  ============================== */
  triggerHaptic() {
    // Vérifie si le navigateur supporte l'API Vibration
    // Note: iOS ne supporte pas navigator.vibrate, Android oui.
    // Les appareils en mode silencieux/ne pas déranger bloquent souvent ceci au niveau OS.
    if (window.navigator && window.navigator.vibrate) {
      window.navigator.vibrate(2); // 2ms = clic très subtil
    }
  }
  
  /* ==============================
     LOGIQUE HEADER IMMERSIF
  ============================== */
  updateHeaderState(panelId) {
    if (!this.header) return;

    // Si on est sur la racine (root), le header est transparent
    if (panelId === 'root') {
      this.header.classList.add('is-transparent');
    } else {
      this.header.classList.remove('is-transparent');
    }
  }

  /* ==============================
     OBSERVER TITRE MENU
  ============================== */
  initBannerTitleObserver() {
    const rootPanel = this.drawer.querySelector('[data-panel="root"]');
    const hero = rootPanel?.querySelector('.mobile-nav-hero');
    const rootBreadcrumb = this.breadcrumbs?.querySelector('.mobile-nav-breadcrumb--root');
    if (!hero || !rootBreadcrumb) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          rootBreadcrumb.classList.remove('is-hidden-over-white');
        } else {
          rootBreadcrumb.classList.add('is-hidden-over-white');
        }
      });
    }, {
      root: rootPanel,
      threshold: 0.1
    });

    observer.observe(hero);
  }

  /* ==============================
     OPEN / CLOSE
  ============================== */
  
  toggle() {
    if (this.drawer.classList.contains('is-open')) {
      this.close();
    } else {
      this.open();
    }
  }
  
  open() {
    this.triggerElement = document.activeElement;

    document.body.classList.add('mobile-nav-open');
    this.drawer.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');
    this.drawer.setAttribute('aria-hidden', 'false');
    
    this.backBtn.classList.add('is-hidden');
    this.closeBtn.classList.remove('is-hidden');
    
    if (this.currentPanel) {
      this.currentPanel.scrollTop = 0;
    }

    requestAnimationFrame(() => {
      this.closeBtn.focus();
    });
  }
  
  close() {
    document.body.classList.remove('mobile-nav-open');
    this.drawer.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.drawer.setAttribute('aria-hidden', 'true');
    
    setTimeout(() => {
      this.resetToRoot();
    }, 400);
    
    if (this.triggerElement) {
      this.triggerElement.focus();
    }
  }
  
  /* ==============================
     NAVIGATION CORE
  ============================== */
  
  navigateToPanel(panelId, title) {
    const targetPanel = this.drawer.querySelector(`[data-panel="${panelId}"]`);
    if (!targetPanel) return;
    
    const level = parseInt(targetPanel.getAttribute('data-level'), 10);
    
    if (this.currentPanel) {
      this.currentPanel.classList.remove('is-active');
    }
    
    targetPanel.classList.add('is-active');
    targetPanel.scrollTop = 0;
    this.currentPanel = targetPanel;
    this.panelHistory.push(panelId);
    
    this.updateBreadcrumbs(level, title);
    this.updateHeaderState(panelId);

    
    const rootBreadcrumb = this.breadcrumbs?.querySelector('.mobile-nav-breadcrumb--root');
    if (rootBreadcrumb) rootBreadcrumb.classList.remove('is-hidden-over-white');
    
    this.backBtn.classList.remove('is-hidden');
    this.closeBtn.classList.add('is-hidden');
  }
  
  goBack() {
    if (this.panelHistory.length <= 1) return;
    
    this.panelHistory.pop();
    
    const previousPanelId = this.panelHistory[this.panelHistory.length - 1];
    const previousPanel = this.drawer.querySelector(`[data-panel="${previousPanelId}"]`);
    
    if (!previousPanel) return;
    
    if (this.currentPanel) {
      this.currentPanel.classList.remove('is-active');
    }
    
    previousPanel.classList.add('is-active');
    this.currentPanel = previousPanel;
    
    this.rebuildBreadcrumbs();
    this.updateHeaderState(previousPanelId);
    
    if (this.panelHistory.length === 1) {
      this.backBtn.classList.add('is-hidden');
      this.closeBtn.classList.remove('is-hidden');
    }
  }

  navigateToLevel(targetLevel) {
    const targetIndex = this.panelHistory.findIndex((_, index) => {
      const panelId = this.panelHistory[index];
      const panel = this.drawer.querySelector(`[data-panel="${panelId}"]`);
      return panel && parseInt(panel.getAttribute('data-level'), 10) === targetLevel;
    });
    
    if (targetIndex === -1) return;
    
    this.panelHistory = this.panelHistory.slice(0, targetIndex + 1);
    
    const targetPanelId = this.panelHistory[this.panelHistory.length - 1];
    const targetPanel = this.drawer.querySelector(`[data-panel="${targetPanelId}"]`);
    
    if (!targetPanel) return;
    
    if (this.currentPanel) {
      this.currentPanel.classList.remove('is-active');
    }
    
    targetPanel.classList.add('is-active');
    this.currentPanel = targetPanel;
    
    this.rebuildBreadcrumbs();
    this.updateHeaderState(targetPanelId);
    
    if (this.panelHistory.length === 1) {
      this.backBtn.classList.add('is-hidden');
      this.closeBtn.classList.remove('is-hidden');
    } else {
      this.backBtn.classList.remove('is-hidden');
      this.closeBtn.classList.add('is-hidden');
    }
  }
  
  /* ==============================
     BREADCRUMBS LOGIC
  ============================== */
  
  updateBreadcrumbs(level, title) {
    const allBreadcrumbs = this.breadcrumbs.querySelectorAll('.mobile-nav-breadcrumb');
    allBreadcrumbs.forEach(b => b.classList.remove('is-active'));
    
    let existingBreadcrumb = this.breadcrumbs.querySelector(`[data-level="${level}"]`);
    
    if (!existingBreadcrumb) {
      const breadcrumb = document.createElement('button');
      breadcrumb.type = 'button';
      breadcrumb.className = 'mobile-nav-breadcrumb is-active';
      breadcrumb.setAttribute('data-level', level);
      breadcrumb.textContent = title;
      this.breadcrumbs.appendChild(breadcrumb);
    } else {
      existingBreadcrumb.classList.add('is-active');
      existingBreadcrumb.textContent = title;
    }
    
    const deepBreadcrumbs = this.breadcrumbs.querySelectorAll(`.mobile-nav-breadcrumb[data-level]`);
    deepBreadcrumbs.forEach(b => {
      const bLevel = parseInt(b.getAttribute('data-level'), 10);
      if (bLevel > level) b.remove();
    });
    
    this.breadcrumbs.scrollLeft = this.breadcrumbs.scrollWidth;
  }
  
  rebuildBreadcrumbs() {
    const breadcrumbsToRemove = this.breadcrumbs.querySelectorAll('.mobile-nav-breadcrumb:not(.mobile-nav-breadcrumb--root)');
    breadcrumbsToRemove.forEach(b => b.remove());
    
    const allBreadcrumbs = this.breadcrumbs.querySelectorAll('.mobile-nav-breadcrumb');
    allBreadcrumbs.forEach(b => b.classList.remove('is-active'));
    
    this.panelHistory.forEach((panelId, index) => {
      if (index === 0) {
        const rootBreadcrumb = this.breadcrumbs.querySelector('.mobile-nav-breadcrumb--root');
        if (this.panelHistory.length === 1) {
          rootBreadcrumb.classList.add('is-active');
        }
      } else {
        const panel = this.drawer.querySelector(`[data-panel="${panelId}"]`);
        if (panel) {
          const level = parseInt(panel.getAttribute('data-level'), 10);
          const title = panel.getAttribute('data-title');
          
          const breadcrumb = document.createElement('button');
          breadcrumb.type = 'button';
          breadcrumb.className = 'mobile-nav-breadcrumb';
          breadcrumb.setAttribute('data-level', level);
          breadcrumb.textContent = title;
          
          if (index === this.panelHistory.length - 1) {
            breadcrumb.classList.add('is-active');
          }
          
          this.breadcrumbs.appendChild(breadcrumb);
        }
      }
    });
    
    this.breadcrumbs.scrollLeft = this.breadcrumbs.scrollWidth;
  }
  
  resetToRoot() {
    this.panels.forEach(panel => {
      panel.classList.remove('is-active');
    });
    
    const rootPanel = this.drawer.querySelector('[data-panel="root"]');
    if (rootPanel) {
      rootPanel.classList.add('is-active');
      this.currentPanel = rootPanel;
    }
    
    this.panelHistory = ['root'];
    
    this.updateHeaderState('root');
    this.backBtn.classList.add('is-hidden');
    this.closeBtn.classList.remove('is-hidden');
    
    const breadcrumbsToRemove = this.breadcrumbs.querySelectorAll('.mobile-nav-breadcrumb:not(.mobile-nav-breadcrumb--root)');
    breadcrumbsToRemove.forEach(b => b.remove());
    
    const rootBreadcrumb = this.breadcrumbs.querySelector('.mobile-nav-breadcrumb--root');
    if (rootBreadcrumb) {
      rootBreadcrumb.classList.add('is-active');
    }
  }
}

// Initialization Safe
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.mobileNavDrawer = new MobileNavDrawer();
  });
} else {
  window.mobileNavDrawer = new MobileNavDrawer();
}